/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';

export default function Navbar({ page, onNavigate, onBack }) {
  const [scrolled, setScrolled]     = useState(false);
  const [user, setUser]             = useState(null);
  const [menuOpen, setMenuOpen]     = useState(false);
  const [isMobile, setIsMobile]     = useState(window.innerWidth < 768);
  const [mode, setMode]             = useState(() => localStorage.getItem('havenly_mode') || 'hote');
  const [hasPublishedListing, setHasPublishedListing] = useState(false);
  const menuRef = useRef(null);
  const [isManager, setIsManager] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [newConfirmed, setNewConfirmed] = useState(0);
  const [hoveredNav, setHoveredNav] = useState(null);

  const switchMode = (m) => {
    setMode(m);
    localStorage.setItem('havenly_mode', m);
    setMenuOpen(false);
    // ✅ Si passage en mode voyageur → SearchPage (protégée)
    onNavigate(m === 'voyageur' ? 'search' : 'home');
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onResize); };
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setUser(session?.user ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => setUser(session?.user ?? null));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) { setIsManager(false); return; }
    supabase.from('host_profiles').select('is_manager').eq('id', user.id).single()
      .then(({ data }) => setIsManager(data?.is_manager === true));
  }, [user]);

  useEffect(() => {
    if (!user) { setUnreadMessages(0); return; }
    const loadUnread = async () => {
      const { data } = await supabase.from('messages').select('id').neq('sender_id', user.id).is('read_at', null);
      setUnreadMessages(data?.length || 0);
    };
    loadUnread();
    const ch = supabase.channel('navbar-unread')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload) => {
        if (payload.new.sender_id !== user.id) setUnreadMessages(n => n + 1);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'messages' }, () => { loadUnread(); })
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [user]);

  useEffect(() => {
    if (!user) { setNewConfirmed(0); return; }
    const loadConfirmed = async () => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase.from('bookings').select('id').eq('guest_id', user.id).eq('status', 'confirmed').gte('updated_at', since);
      const seen = JSON.parse(localStorage.getItem('havenly_seen_confirmed') || '[]');
      const unseen = (data || []).filter(b => !seen.includes(b.id));
      setNewConfirmed(unseen.length);
    };
    loadConfirmed();
    const ch2 = supabase.channel('navbar-bookings')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'bookings', filter: 'status=eq.confirmed' }, (payload) => {
        if (payload.new.guest_id === user.id) {
          const seen = JSON.parse(localStorage.getItem('havenly_seen_confirmed') || '[]');
          if (!seen.includes(payload.new.id)) setNewConfirmed(n => n + 1);
        }
      }).subscribe();
    return () => supabase.removeChannel(ch2);
  }, [user]);

  useEffect(() => {
    if (user && mode === 'hote') {
      supabase.from('listings').select('id').eq('host_id', user.id).eq('status', 'published').limit(1)
        .then(({ data }) => setHasPublishedListing((data?.length || 0) > 0));
    }
  }, [user, mode]);

  useEffect(() => {
    if (!menuOpen) return;
    let timer;
    const handleOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    timer = setTimeout(() => { document.addEventListener('mousedown', handleOutside); }, 0);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', handleOutside); };
  }, [menuOpen]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setMenuOpen(false);
    setMode('voyageur');
    localStorage.setItem('havenly_mode', 'voyageur');
    localStorage.removeItem('havenly_pending_user_type');
    localStorage.removeItem('havenly_is_manager');
    onNavigate('home');
  };

  const avatarUrl = user?.user_metadata?.avatar_url || user?.user_metadata?.picture || null;
  const initials  = user?.user_metadata?.full_name
    ? user.user_metadata.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : user?.email?.slice(0, 2).toUpperCase() ?? '?';

  const navTo = (dest) => { onNavigate(dest); setMenuOpen(false); };

  const hoteLinks = [
    { label: 'Tableau de bord',   dest: 'dashboard'  },
    { label: 'Mes annonces',      dest: 'mylistings' },
    { label: 'Messages',          dest: 'messages'   },
    ...(hasPublishedListing ? [{ label: 'Discuter entre hôtes en direct', dest: 'community' }] : []),
    { label: 'Parrainer un hôte', dest: 'referral'   },
    { label: 'Comment ça marche', dest: 'howitworks' },
    { label: 'Paramètres',        dest: 'settings'   },
  ];

  const voyageurLinks = [
    { label: 'Explorer les logements', dest: 'search'     },
    { label: 'Mes réservations',       dest: 'mybookings' },
    { label: 'Messages',               dest: 'messages'   },
    { label: 'Aide voyageur',          dest: 'voyageur'   },
    { label: 'Paramètres',             dest: 'settings'   },
    { label: 'Comment ça marche',      dest: 'howitworks' },
    { label: 'Devenir hôte',           dest: 'becomehost' },
  ];

  const activeLinks = mode === 'hote' ? hoteLinks : voyageurLinks;

  const Switcher = ({ compact = false }) => (
    <div style={{ display: 'flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 24, padding: 3, gap: 2, flexShrink: 0 }}>
      {[
        { key: 'voyageur', label: compact ? 'V' : 'Voyageur' },
        { key: 'hote',     label: compact ? (isManager ? 'C' : 'H') : (isManager ? 'Conciergerie' : 'Hôte') },
      ].map(({ key, label }) => (
        <button key={key} onClick={() => user ? switchMode(key) : navTo('auth')}
          style={{ padding: compact ? '5px 10px' : '5px 14px', borderRadius: 20, border: 'none', fontSize: compact ? 14 : 13, fontWeight: 500, fontFamily: 'var(--sans)', cursor: 'pointer', transition: 'all 0.18s', background: mode === key ? (key === 'hote' ? 'var(--green)' : 'var(--ink)') : 'transparent', color: mode === key ? '#fff' : 'var(--ink-3)', whiteSpace: 'nowrap' }}>
          {label}
        </button>
      ))}
    </div>
  );

  const AvatarBtn = ({ size = 28 }) => (
    user ? (
      avatarUrl
        ? <img src={avatarUrl} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />
        : <div style={{ width: size, height: size, borderRadius: '50%', background: mode === 'hote' ? 'var(--green)' : 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.38, fontWeight: 600, color: '#fff' }}>{initials}</div>
    ) : (
      <div style={{ width: size, height: size, borderRadius: '50%', background: 'var(--surface)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" fill="none" stroke="var(--ink-3)" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
      </div>
    )
  );

  return (
    <>
      <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50000, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 1.5rem', background: 'rgba(255,255,255,0.96)', borderBottom: '0.5px solid rgba(26,26,24,0.10)', backdropFilter: scrolled || page !== 'home' ? 'blur(12px)' : 'none' }}>

        {/* Bouton Retour */}
        {page !== 'home' && onBack && (
          <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--ink-3)', padding: '6px 8px', borderRadius: 8, flexShrink: 0 }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.color = 'var(--ink)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--ink-3)'; }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            {!isMobile && 'Retour'}
          </button>
        )}

        {/* Logo */}
        <div onClick={() => navTo('home')} style={{ fontFamily: 'var(--serif)', fontSize: 22, letterSpacing: '-0.5px', cursor: 'pointer', flexShrink: 0 }}>
          <svg viewBox="0 0 210 52" style={{ height: 22, width: 'auto', display: 'block' }} xmlns="http://www.w3.org/2000/svg">
            <text x="0" y="44" fontFamily="Georgia,'Times New Roman',serif" fontSize="52" fontWeight="400" fontStyle="italic" letterSpacing="-2" fill="currentColor">haven<tspan fill="#1D9E75" dx="-3">ly</tspan></text>
          </svg>
        </div>

        {/* DESKTOP */}
        {!isMobile && (
          // ✅ FIX 2: gap 6 → 24 pour espacer les onglets
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            {user && <Switcher />}

            {/* ✅ FIX 1: "Voyageur" redirige vers 'search' au lieu de 'voyageur-home' */}
            {!user && (
              <button onClick={() => navTo('howitworks')}
                style={{ fontSize: 14, color: 'var(--ink-3)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--sans)', padding: '4px 0' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--ink)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-3)'}>
                Comment ça marche
              </button>
            )}
            {!user && (
              <button onClick={() => { localStorage.setItem('havenly_mode', 'hote'); navTo('home'); }}
                style={{ fontSize: 14, color: 'var(--ink-3)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--sans)', padding: '4px 0' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--ink)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-3)'}>
                Hôte
              </button>
            )}
            {!user && (
              <button onClick={() => navTo('conciergeries')}
                style={{ fontSize: 14, color: 'var(--ink-3)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--sans)', padding: '4px 0' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--ink)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-3)'}>
                Conciergerie
              </button>
            )}
            {!user && (
              // ✅ Voyageur → auth si non connecté (SearchPage protégée)
              <button onClick={() => navTo('auth')}
                style={{ fontSize: 14, color: 'var(--ink-3)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--sans)', padding: '4px 0' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--ink)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-3)'}>
                Voyageur
              </button>
            )}

            <div ref={menuRef} style={{ position: 'relative' }}>
              <button onClick={() => setMenuOpen(prev => !prev)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: '1px solid var(--border)', borderRadius: 24, padding: '5px 10px 5px 8px', cursor: 'pointer' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--ink-3)" strokeWidth="2">
                  <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
                </svg>
                <AvatarBtn size={28} />
              </button>

              {menuOpen && (
                <div style={{ position: 'absolute', top: 48, right: 0, background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', minWidth: 230, zIndex: 9999, overflow: 'hidden' }}>
                  {user ? (
                    <>
                      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{user.user_metadata?.full_name || 'Mon compte'}</div>
                        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{user.email}</div>
                        <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, background: mode === 'hote' ? '#E8F5F0' : '#EEEDFE', borderRadius: 20, padding: '3px 10px' }}>
                          <div style={{ width: 6, height: 6, borderRadius: '50%', background: mode === 'hote' ? 'var(--green)' : '#534AB7' }} />
                          <span style={{ fontSize: 12, fontWeight: 500, color: mode === 'hote' ? '#0F6E56' : '#534AB7' }}>
                            Mode {mode === 'hote' ? (isManager ? 'Conciergerie' : 'Hôte') : 'Voyageur'}
                          </span>
                        </div>
                      </div>
                      <div style={{ padding: '6px' }}>
                        {activeLinks.map(item => (
                          <button key={item.label} onClick={() => navTo(item.dest)}
                            style={{ width: '100%', padding: '11px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, color: 'var(--ink)', cursor: 'pointer', fontFamily: 'var(--sans)', borderRadius: 8 }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {item.label}
                              {item.dest === 'messages' && unreadMessages > 0 && (
                                <span style={{ background: '#E2394B', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: '50%', minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>{unreadMessages}</span>
                              )}
                              {item.dest === 'mybookings' && newConfirmed > 0 && (
                                <span style={{ background: '#0F6E56', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: '50%', minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>{newConfirmed}</span>
                              )}
                            </span>
                          </button>
                        ))}
                      </div>
                      <div style={{ borderTop: '1px solid var(--border)', padding: '6px' }}>
                        <button onClick={handleSignOut}
                          style={{ width: '100%', padding: '11px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, color: '#A32D2D', cursor: 'pointer', fontFamily: 'var(--sans)', borderRadius: 8 }}
                          onMouseEnter={e => e.currentTarget.style.background = '#FCEBEB'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                          Se déconnecter
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ padding: '6px' }}>
                        {[{ label: 'Se connecter', dest: 'auth', mode: null }, { label: "S'inscrire", dest: 'auth', mode: 'register' }].map(item => (
                          <button key={item.label} onClick={() => { if (item.mode) { localStorage.setItem('authRedirect', 'home'); } onNavigate(item.dest, item.mode); setMenuOpen(false); }}
                            style={{ width: '100%', padding: '11px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, fontWeight: item.label === 'Se connecter' ? 500 : 400, color: 'var(--ink)', cursor: 'pointer', fontFamily: 'var(--sans)', borderRadius: 8 }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                            {item.label}
                          </button>
                        ))}
                      </div>
                      <div style={{ borderTop: '1px solid var(--border)', padding: '6px' }}>
                        {[
                          { label: 'Comment ça marche', dest: 'howitworks' },
                          { label: 'Devenir hôte',       dest: 'becomehost' },
                          { label: 'Explorer',           dest: 'search'     },
                        ].map(item => (
                          <button key={item.label} onClick={() => navTo(item.dest)}
                            style={{ width: '100%', padding: '11px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, color: 'var(--ink-3)', cursor: 'pointer', fontFamily: 'var(--sans)', borderRadius: 8 }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* MOBILE */}
        {isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {user && <Switcher compact={true} />}
            <button onClick={() => setMenuOpen(prev => !prev)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid var(--border)', borderRadius: 20, padding: '6px 10px', cursor: 'pointer', flexShrink: 0 }}>
              {menuOpen
                ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
              }
              <AvatarBtn size={24} />
            </button>
          </div>
        )}
      </nav>

      {/* MENU MOBILE PLEIN ÉCRAN */}
      {isMobile && menuOpen && (
        <div style={{ position: 'fixed', top: 60, left: 0, right: 0, bottom: 0, background: 'var(--white)', zIndex: 49999, overflowY: 'auto', padding: '1.5rem' }}>
          {user ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px', background: 'var(--surface)', borderRadius: 14, marginBottom: 16 }}>
                <AvatarBtn size={44} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.user_metadata?.full_name || 'Mon compte'}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
                {[
                  { key: 'voyageur', label: 'Mode Voyageur', desc: 'Explorer & réserver' },
                  { key: 'hote',     label: isManager ? 'Mode Conciergerie' : 'Mode Hôte', desc: isManager ? 'Gérer mes biens' : 'Gérer mes annonces' },
                ].map(({ key, label, desc }) => (
                  <button key={key} onClick={() => switchMode(key)}
                    style={{ padding: '14px 12px', border: '2px solid', borderColor: mode === key ? (key === 'hote' ? 'var(--green)' : '#534AB7') : 'var(--border)', borderRadius: 12, background: mode === key ? (key === 'hote' ? '#E8F5F0' : '#EEEDFE') : 'var(--white)', cursor: 'pointer', fontFamily: 'var(--sans)', textAlign: 'left' }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: mode === key ? (key === 'hote' ? '#0F6E56' : '#534AB7') : 'var(--ink)', marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{desc}</div>
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 16 }}>
                {activeLinks.map(item => (
                  <button key={item.label} onClick={() => {
                      if (item.dest === 'messages') setUnreadMessages(0);
                      if (item.dest === 'mybookings' && newConfirmed > 0) {
                        supabase.from('bookings').select('id').eq('guest_id', user.id).eq('status', 'confirmed')
                          .then(({ data }) => { const ids = (data || []).map(b => b.id); localStorage.setItem('havenly_seen_confirmed', JSON.stringify(ids)); setNewConfirmed(0); });
                      }
                      navTo(item.dest);
                    }}
                    style={{ width: '100%', padding: '14px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 15, color: 'var(--ink)', cursor: 'pointer', fontFamily: 'var(--sans)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    {item.label}
                    {item.dest === 'messages' && unreadMessages > 0 && <span style={{ background: '#E2394B', color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '2px 8px' }}>{unreadMessages}</span>}
                    {item.dest === 'mybookings' && newConfirmed > 0 && <span style={{ background: '#0F6E56', color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '2px 8px' }}>{newConfirmed}</span>}
                  </button>
                ))}
              </div>
              <div style={{ height: 1, background: 'var(--border)', marginBottom: 8 }} />
              <button onClick={handleSignOut}
                style={{ width: '100%', padding: '14px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 15, color: '#A32D2D', cursor: 'pointer', fontFamily: 'var(--sans)', borderRadius: 10 }}>
                Se déconnecter
              </button>
            </>
          ) : (
            <>
              <button onClick={() => navTo('auth')}
                style={{ width: '100%', padding: '14px', background: 'var(--green)', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 500, color: '#fff', cursor: 'pointer', fontFamily: 'var(--sans)', marginBottom: 12 }}>
                Se connecter / S'inscrire
              </button>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  { label: 'Comment ça marche', dest: 'howitworks' },
                  { label: 'Hôte',              dest: 'home', preAction: () => localStorage.setItem('havenly_mode', 'hote') },
                  { label: 'Conciergerie',      dest: 'conciergeries' },
                  // ✅ FIX 1 mobile: Voyageur → search
                  { label: 'Voyageur',          dest: 'auth' },
                  { label: 'Explorer les logements', dest: 'auth' },
                ].map(item => (
                  <button key={item.label} onClick={() => { if (item.preAction) item.preAction(); navTo(item.dest); }}
                    style={{ width: '100%', padding: '14px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 15, color: 'var(--ink)', cursor: 'pointer', fontFamily: 'var(--sans)', borderRadius: 10 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    {item.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
